// src/services/chatLock.js
//
// 🔒 THE CHAT LOCK, ON THE WEB — the phone's lock, respected here.
//
// 🟥 THE WEB HAD NO PIN AT ALL. A chat locked on the phone opened here with one click — every
//    message, every photo — and the old lock was worse than nothing: it was the PIN itself, in
//    plain text, on the chat document (chats/{id}.security.{uid}.pinCode), which this client
//    could read like any member could.
// 🟢 The phone keeps the lock in users/{uid}/private/chatLocks — owner-only (firestore rules) —
//    as a salted, iterated SHA-512 of the PIN, never the PIN. This module listens to that one
//    document and checks a PIN the same way the phone does (services/chatLockCore.ts there:
//    H(salt‖pin), then H(h‖salt) iter−1 times, base64). A locked chat is drawn as its lock until
//    the PIN is right; its row says "Locked chat"; its notifications say nothing.
//    An unlock is this tab's, in memory: "every time" locks again when the chat closes or the
//    tab is hidden; "after N minutes" N minutes after you left it. Five wrong guesses are free,
//    then each waits twice as long (30 s → 15 min).

import { useEffect, useState } from 'react';
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

export const FREE_TRIES = 5;
export const BACKOFF_BASE_MS = 30_000;
export const BACKOFF_CAP_MS = 15 * 60_000;
export const OPEN_GRACE_MS = 15_000;

const utf8 = (s) => new TextEncoder().encode(String(s));
const cat = (a, b) => { const o = new Uint8Array(a.length + b.length); o.set(a, 0); o.set(b, a.length); return o; };

/** the phone's hash, exactly */
export function hashPin(pin, saltB64, iter) {
  const salt = util.decodeBase64(saltB64);
  let h = nacl.hash(cat(salt, utf8(pin)));
  for (let i = 1; i < iter; i++) h = nacl.hash(cat(h, salt));
  return util.encodeBase64(h);
}
export function sameText(a, b) {
  const x = String(a || ''), y = String(b || '');
  let d = x.length ^ y.length;
  for (let i = 0; i < Math.max(x.length, y.length); i++) d |= (x.charCodeAt(i) || 0) ^ (y.charCodeAt(i) || 0);
  return d === 0;
}
export const backoffMs = (fails) => (fails < FREE_TRIES ? 0 : Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * Math.pow(2, fails - FREE_TRIES)));

let _uid = null;
let _locks = {};
let _off = null;
const _until = new Map();   // chat → open until (Infinity while it is on screen)
const _held = new Map();
let _tries = {};
const subs = new Set();
const tell = () => { for (const f of Array.from(subs)) { try { f(); } catch {} } };
export const subscribeChatLock = (f) => { subs.add(f); return () => subs.delete(f); };

const triesKey = () => `allo_chatlock_tries_${_uid}`;
const loadTries = () => { try { _tries = JSON.parse(localStorage.getItem(triesKey()) || '{}') || {}; } catch { _tries = {}; } };
const saveTries = () => { try { localStorage.setItem(triesKey(), JSON.stringify(_tries)); } catch {} };

const clean = (r) => (r && typeof r === 'object' && typeof r.salt === 'string' && typeof r.hash === 'string' && r.salt && r.hash
  ? { salt: r.salt, hash: r.hash, iter: Number(r.iter) > 0 ? Number(r.iter) : 500, len: Number(r.len) === 6 ? 6 : 4, timing: r.timing === 'after' ? 'after' : 'always', delayMin: Math.max(1, Number(r.delayMin) || 5) }
  : null);

/** this account's lock list, live */
export function watchChatLocks(uid) {
  if (_uid === uid && _off) return;
  try { _off && _off(); } catch {}
  _uid = uid || null; _locks = {}; _until.clear(); _held.clear();
  if (!uid) { tell(); return; }
  loadTries();
  _off = onSnapshot(doc(db, 'users', uid, 'private', 'chatLocks'), (snap) => {
    const raw = (snap.exists() && snap.data() && snap.data().locks) || {};
    const next = {};
    Object.keys(raw).forEach((k) => { const r = clean(raw[k]); if (r) next[k] = r; });
    _locks = next;
    tell();
  }, () => {});
}

export const isChatLocked = (id) => !!id && !!_locks[id];
export const isChatOpenNow = (id) => !document.hidden && (_until.get(id) || 0) > Date.now();
export const chatNeedsUnlock = (id) => isChatLocked(id) && !isChatOpenNow(id);
export const chatLockLen = (id) => (_locks[id] ? _locks[id].len : 4);
export const chatLockWait = (id) => Math.max(0, ((_tries[id] && _tries[id].until) || 0) - Date.now());

/** check a PIN: right → open (guesses forgotten); wrong → counted */
export function verifyChatPin(id, pin) {
  const rec = _locks[id];
  const wait = chatLockWait(id);
  if (!rec) return { ok: true };
  if (wait > 0) return { ok: false, waitMs: wait, left: 0 };
  if (/^\d{4}$|^\d{6}$/.test(String(pin)) && sameText(hashPin(pin, rec.salt, rec.iter), rec.hash)) {
    delete _tries[id]; saveTries();
    _until.set(id, _held.get(id) ? Infinity : Date.now() + (rec.timing === 'always' ? OPEN_GRACE_MS : rec.delayMin * 60_000));
    tell();
    return { ok: true };
  }
  const fails = ((_tries[id] && _tries[id].fails) || 0) + 1;
  _tries[id] = { fails, until: Date.now() + backoffMs(fails) };
  saveTries(); tell();
  return { ok: false, waitMs: chatLockWait(id), left: Math.max(0, FREE_TRIES - fails) };
}

/** a pane shows this chat: held open while it does */
export function holdChatOpen(id) {
  if (!id) return () => {};
  _held.set(id, (_held.get(id) || 0) + 1);
  if ((_until.get(id) || 0) > Date.now()) _until.set(id, Infinity);
  let done = false;
  return () => {
    if (done) return; done = true;
    const n = (_held.get(id) || 0) - 1;
    if (n > 0) { _held.set(id, n); return; }
    _held.delete(id);
    const rec = _locks[id];
    if (!rec || (_until.get(id) || 0) <= Date.now()) return;
    if (rec.timing === 'always') _until.delete(id); else _until.set(id, Date.now() + rec.delayMin * 60_000);
    tell();
  };
}

/* the tab hidden: "every time" locks; "after N minutes" starts counting */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      Array.from(_until.keys()).forEach((id) => {
        const rec = _locks[id];
        if (!rec || rec.timing === 'always') _until.delete(id);
        else _until.set(id, Math.min(_until.get(id) || 0, Date.now() + rec.delayMin * 60_000));
      });
    } else {
      Array.from(_until.keys()).forEach((id) => { if ((_until.get(id) || 0) <= Date.now()) _until.delete(id); else if (_held.get(id)) _until.set(id, Infinity); });
    }
    tell();
  });
}

/** re-render when the answer for this chat changes */
export function useChatLock(id) {
  const [, force] = useState(0);
  useEffect(() => subscribeChatLock(() => force((n) => n + 1)), []);
  return { locked: isChatLocked(id), needs: chatNeedsUnlock(id) };
}
